<template>
  <div
    class="prose prose-neutral flex min-h-screen min-w-screen items-center justify-center overflow-y-auto bg-light-0 py-6 dark:bg-dark-0"
  >
    <div
      class="deprecation-gate z-10 flex flex-col items-center justify-center"
    >
      <div
        class="gate-container mx-auto flex w-full max-w-2xl flex-col gap-4 rounded-lg bg-light-1 px-8 py-10 sm:px-14"
      >
        <div class="flex flex-col gap-4">
          <h1 class="mb-0 mt-0">{{ copy.title }}</h1>
          <p class="my-0">{{ copy.message }}</p>
          <PrimaryButton
            v-if="isNative"
            class="w-full"
            :label="i18n.openV2"
            @click="openV2"
          />
          <a
            v-else
            :href="v2Url"
            class="w-full rounded-full bg-primary-2 px-5 py-2 text-center text-base font-medium text-white no-underline hover:bg-primary-1 dark:bg-primary-1 dark:hover:bg-primary-0"
          >
            {{ i18n.web.openV2.replace('{host}', v2Host) }}
          </a>
          <p
            v-if="launchFailed"
            class="text-sm text-light-error dark:text-dark-error"
          >
            {{ i18n.openV2Failed }}
          </p>
        </div>
        <template v-if="isNative">
          <div class="border-t border-light-4 dark:border-dark-2"></div>
          <FallbackConfirmation
            :v2-launch-attempted="launchAttempted"
            :v2-launch-failed="launchFailed"
            @continue-to-v1="onContinueToV1"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import PrimaryButton from '@/components/UI/Button/PrimaryButton.vue';
import FallbackConfirmation from '@/components/DeprecationGate/FallbackConfirmation.vue';
import { V2Launcher, getV2PackageId } from '@/native/plugins/V2Launcher';
import { getDeprecationGateTexts } from '@/utils/deprecationGate/texts';
import envConfig from '@/config/runtimeConfig';
import { getV2Host, getV2Url } from '@/utils/deprecationGate/v2Url';

const emit = defineEmits<{
  (e: 'continue-to-v1'): void;
}>();

const i18n = getDeprecationGateTexts();
const isNative = Capacitor.isNativePlatform();
const route = useRoute();
const v2Url = computed(() => getV2Url(envConfig.bxtoolV2Url, route));
// Web users only need to know that the address changed; the native app keeps the v1/v2 wording.
const copy = isNative ? i18n : i18n.web;
const v2Host = getV2Host(envConfig.bxtoolV2Url);

const launchAttempted = ref(false);
const launchFailed = ref(false);

async function openV2() {
  launchAttempted.value = true;
  try {
    const packageId = await getV2PackageId();
    await V2Launcher.launch({ packageId });
    launchFailed.value = false;
  } catch {
    launchFailed.value = true;
  }
}

function onContinueToV1() {
  emit('continue-to-v1');
}
</script>
